import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { formatTransactionData } from './formatTransactionData'
import { useTheme } from '@/hooks/useTheme'
import { Transaction } from '@/domain/transactions'
import { TransactionStateTypes } from '@/utils/transactionUtils'

interface TransactionInfosProps {
  transaction?: Transaction
  isLoading?: boolean
}

export const TransactionInfos: React.FC<TransactionInfosProps> = ({
  transaction,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Early return for loading state
  if (isLoading || !transaction) {
    return (
      <BaseView bgColor="background.cardItemRoot" rounded={16} p={16} gap={12}>
        <BaseView
          style={{
            height: 200,
            backgroundColor: theme.colors.background.secondaryGray + '20',
            borderRadius: 8,
          }}
        />
      </BaseView>
    )
  }

  const transactionInfosList = t && formatTransactionData(transaction, t)

  const statusColor = (status: TransactionStateTypes) => {
    switch (status) {
      case 'refunded':
        return theme.colors.label.description
      case 'completed':
        return theme.colors.label.flagProfit
      case 'processing':
        return theme.colors.label.description
      case 'declined':
      case 'cancelled':
        return theme.colors.label.flagLosses
      default:
        return theme.colors.label.default
    }
  }

  return (
    <BaseView rounded={16} p={0} gap={12}>
      {transactionInfosList?.map((transactionInfo) => {
        const {
          isVisible,
          id,
          label,
          value,
          secondLineValue,
          secondaryValue,
          Icon,
          iconPosition = 'right',
          unformattedValue,
          key,
        } = transactionInfo

        if (!isVisible) return null

        return (
          <BaseView
            key={`${key}-${id}`}
            row
            justify="space-between"
            align="center"
            my={22}
            style={{
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.background.textInput,
              borderBottomStyle: 'solid',
              paddingBottom: 22,
            }}
          >
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
              style={{ maxWidth: '42%' }}
            >
              {label}
            </BaseText>

            <BaseView row align="center" gap={6}>
              {Icon && iconPosition === 'left' && <Icon />}

              <BaseView align="flex-end" gap={4}>
                <BaseText
                  fontSize={16}
                  fontFamily="medium"
                  color={
                    key === 'status' && unformattedValue
                      ? statusColor(unformattedValue)
                      : 'label.default'
                  }
                  textAlign="right"
                  style={{
                    wordBreak: 'break-all',
                  }}
                >
                  {value}
                  {secondLineValue && `\n${secondLineValue}`}
                </BaseText>

                {secondaryValue && (
                  <BaseText
                    fontSize={14}
                    fontFamily="regular"
                    color="label.description"
                    textAlign="right"
                  >
                    {secondaryValue}
                  </BaseText>
                )}
              </BaseView>

              {Icon && iconPosition === 'right' && <Icon />}
            </BaseView>
          </BaseView>
        )
      })}
    </BaseView>
  )
}
